import React, { useEffect } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Image,
    Button,
    Divider,
    HStack,
    Flex,
    Icon,
    useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { FaUser } from "react-icons/fa";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { brandConfig } from "../brandConfig";

const CourseModule = () => {
    const navigate = useNavigate();
    const cardBg = useColorModeValue("white", "gray.700");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box bg={brandConfig.colors.background.light} minHeight="100vh">
            <Box 
                bg="white" 
                borderBottom="1px solid" 
                borderColor="gray.200"
                position="sticky"
                top={0}
                zIndex={100}
            >
                <Container maxW="7xl" px={{ base: 4, md: 6 }}>
                    <Flex 
                        justify="space-between" 
                        align="center" 
                        minH={{ base: "70px", md: "90px" }}
                        py={{ base: 1, md: 1 }}
                    >
                    <HStack spacing={2} cursor="pointer" onClick={() => navigate('/')}>
                        <Image 
                            src="/surgready/logo.png" 
                            alt="SurgReady" 
                            h={{ base: "60px", md: "80px" }}
                            objectFit="contain"
                        />
                    </HStack>
                    <HStack spacing={8}>
                        <Button
                            variant="ghost"
                            fontSize="lg"
                            fontWeight="semibold"
                            color={brandConfig.colors.text.primary}
                            onClick={() => navigate('/all-modules')}
                            _hover={{ bg: "gray.100" }}
                        >
                            All Modules
                        </Button>
                        <Button
                            variant="ghost"
                            fontSize="lg"
                            fontWeight="semibold"
                            color={brandConfig.colors.text.primary}
                            _hover={{ bg: "gray.100" }}
                        >
                            Community
                        </Button>
                        <HStack
                            spacing={3}
                            bg={brandConfig.colors.primary}
                            px={4}
                            py={2}
                            borderRadius="full"
                            cursor="pointer"
                            onClick={() => navigate('/member-profile')}
                            _hover={{ bg: brandConfig.colors.secondary }}
                            transition="all 0.2s"
                        >
                            <Text
                                fontSize="lg"
                                fontWeight="semibold"
                                color="white"
                            >
                                I am SurgReady
                            </Text>
                            <Box
                                bg="white"
                                borderRadius="full"
                                w={10}
                                h={10}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Icon as={FaUser} w={5} h={5} color={brandConfig.colors.primary} />
                            </Box>
                        </HStack>
                    </HStack>
                </Flex>
                </Container>
            </Box>

            <Container maxW="900px" py={{ base: 16, md: 20 }}>
                <VStack spacing={10} align="stretch">
                    {/* Page Title */}
                    <Box textAlign="center" py={8}>
                        <Heading
                            as="h1"
                            size="2xl"
                            mb={4}
                            color={brandConfig.colors.text.primary}
                            fontWeight="bold"
                        >
                            Module 2: Nutrition
                        </Heading>
                        <Text fontSize="lg" color={brandConfig.colors.text.secondary} mb={6}>
                            Your comprehensive guide to optimal surgical preparation
                        </Text>
                        <Image
                            src="/surgready/nutrition-hero.png"
                            alt="Nutrition - healthy foods including salmon, vegetables, and fruits"
                            borderRadius="xl"
                            boxShadow="lg"
                            maxW={{ base: "100%", md: "600px" }}
                            w="100%"
                            h="auto"
                            mx="auto"
                        />
                    </Box>

                    <Divider />

                    {/* Introduction Section */}
                    <Box
                        bg={cardBg}
                        p={8}
                        borderRadius="xl"
                        boxShadow={brandConfig.components.card.shadow}
                        border="1px solid"
                        borderColor={brandConfig.components.card.border}
                    >
                        <Heading as="h2" size="lg" mb={4} color={brandConfig.colors.primary}>
                            Introduction
                        </Heading>
                        <Text fontSize="lg" color={brandConfig.colors.text.primary} lineHeight="tall">
                            Welcome to this essential course module on surgical preparation! In this lesson, you will
                            learn evidence-based strategies to optimize your health before surgery, helping you achieve
                            better outcomes, faster recovery, and reduced complications. We'll cover nutrition,
                            exercise, mental health, and other key factors that contribute to surgical success.
                        </Text>
                    </Box>

                    {/* Lesson Content Section */}
                    <Box>
                        <Heading as="h2" size="xl" mb={6} color={brandConfig.colors.text.primary} textAlign="center">
                            Lesson Content
                        </Heading>

                        {/* Teaching Point 1 */}
                        <VStack
                            spacing={4}
                            align="stretch"
                            bg={cardBg}
                            p={8}
                            mb={6}
                            borderRadius="xl"
                            boxShadow={brandConfig.components.card.shadow}
                            border="1px solid"
                            borderColor={brandConfig.components.card.border}
                        >
                            <Heading as="h3" size="md" color={brandConfig.colors.secondary}>
                                1. Nutrition and Hydration
                            </Heading>
                            <Text fontSize="md" color={brandConfig.colors.text.primary} lineHeight="tall">
                                Proper nutrition plays a crucial role in surgical preparation and recovery. Consuming
                                adequate protein helps your body build and repair tissues, while vitamins and minerals
                                support your immune system. Focus on eating lean proteins, colorful vegetables, whole
                                grains, and staying well-hydrated in the weeks leading up to your surgery.
                            </Text>
                            <Image
                                src="/surgready/running-couple.png"
                                alt="Healthy nutrition and lifestyle"
                                borderRadius="lg"
                                boxShadow="md"
                                maxH="300px"
                                objectFit="cover"
                                mx="auto"
                            />
                            <Text
                                fontSize="sm"
                                color={brandConfig.colors.text.muted}
                                fontStyle="italic"
                                textAlign="center"
                            >
                                Maintaining an active lifestyle and good nutrition supports optimal surgical outcomes
                            </Text>
                        </VStack>

                        {/* Teaching Point 2 */}
                        <VStack
                            spacing={4}
                            align="stretch"
                            bg={cardBg}
                            p={8}
                            mb={6}
                            borderRadius="xl"
                            boxShadow={brandConfig.components.card.shadow}
                            border="1px solid"
                            borderColor={brandConfig.components.card.border}
                        >
                            <Heading as="h3" size="md" color={brandConfig.colors.secondary}>
                                2. Physical Activity and Exercise
                            </Heading>
                            <Text fontSize="md" color={brandConfig.colors.text.primary} lineHeight="tall">
                                Regular physical activity before surgery can significantly improve your recovery time and
                                reduce complications. Even light to moderate exercise, such as walking, swimming, or
                                gentle stretching, helps build strength, improve cardiovascular health, and boost your
                                overall fitness level. Always consult with your healthcare provider about appropriate
                                exercises for your specific situation.
                            </Text>
                            <Image
                                src="/assets/homepage/asian-doctor.png"
                                alt="Medical consultation for exercise guidance"
                                borderRadius="lg"
                                boxShadow="md"
                                maxH="300px"
                                objectFit="cover"
                                mx="auto"
                            />
                            <Text
                                fontSize="sm"
                                color={brandConfig.colors.text.muted}
                                fontStyle="italic"
                                textAlign="center"
                            >
                                Work with your healthcare team to develop a safe exercise plan
                            </Text>
                        </VStack>

                        {/* Teaching Point 3 */}
                        <VStack
                            spacing={4}
                            align="stretch"
                            bg={cardBg}
                            p={8}
                            borderRadius="xl"
                            boxShadow={brandConfig.components.card.shadow}
                            border="1px solid"
                            borderColor={brandConfig.components.card.border}
                        >
                            <Heading as="h3" size="md" color={brandConfig.colors.secondary}>
                                3. Mental Health and Stress Management
                            </Heading>
                            <Text fontSize="md" color={brandConfig.colors.text.primary} lineHeight="tall">
                                Managing stress and maintaining positive mental health are essential components of
                                surgical preparation. Anxiety before surgery is normal, but excessive stress can impact
                                your immune system and recovery. Practice relaxation techniques such as deep breathing,
                                meditation, or mindfulness. Don't hesitate to discuss any concerns with your healthcare
                                team or seek support from family and friends.
                            </Text>
                            <Image
                                src="/surgready/hero-image.png"
                                alt="Patient receiving compassionate care"
                                borderRadius="lg"
                                boxShadow="md"
                                maxH="300px"
                                objectFit="cover"
                                mx="auto"
                            />
                            <Text
                                fontSize="sm"
                                color={brandConfig.colors.text.muted}
                                fontStyle="italic"
                                textAlign="center"
                            >
                                Emotional support and stress management improve surgical outcomes
                            </Text>
                        </VStack>
                    </Box>

                    {/* Summary and Next Steps Section */}
                    <Box
                        bg={brandConfig.colors.background.surface}
                        p={8}
                        borderRadius="xl"
                        boxShadow={brandConfig.components.card.shadow}
                        border="1px solid"
                        borderColor={brandConfig.components.card.border}
                    >
                        <Heading as="h2" size="lg" mb={4} color={brandConfig.colors.primary}>
                            Summary and Next Steps
                        </Heading>
                        <Text fontSize="md" color={brandConfig.colors.text.primary} lineHeight="tall" mb={4}>
                            Congratulations on completing this module! You've learned about the three key pillars of
                            surgical preparation: proper nutrition and hydration, regular physical activity, and mental
                            health management. By focusing on these areas in the weeks leading up to your surgery,
                            you're taking important steps to optimize your health and improve your surgical outcomes.
                        </Text>
                        <Text fontSize="md" color={brandConfig.colors.text.primary} lineHeight="tall" mb={6}>
                            <strong>What to do next:</strong> Review the information from this module and consider which
                            areas you'd like to focus on first. Discuss your surgical preparation plan with your
                            healthcare provider. Remember that small, consistent changes can make a big difference in your
                            surgical journey. Continue to the next module to learn more about specific strategies and
                            techniques for each of these important areas.
                        </Text>
                    </Box>

                    <Divider />

                    {/* Back to Member Homepage Button */}
                    <Box textAlign="center" py={6}>
                        <Button
                            leftIcon={<ChevronLeftIcon />}
                            size="lg"
                            onClick={() => navigate("/member-homepage")}
                            bg={brandConfig.colors.secondary}
                            color="white"
                            px={10}
                            py={6}
                            fontSize="lg"
                            fontWeight="bold"
                            borderRadius="full"
                            _hover={{
                                bg: brandConfig.colors.secondaryHover,
                                transform: "translateY(-2px)",
                                boxShadow: "lg",
                            }}
                        >
                            Return to Member's Homepage
                        </Button>
                    </Box>
                </VStack>
            </Container>

            <FooterWithFourColumns />
        </Box>
    );
};

export default CourseModule;
